// Platform admin audit log screen (archetype: DataView).
//
// Server-side pagination with action + period filters. Every admin mutation
// since P0 lands here (user/workspace/mail actions); unknown future actions
// render their raw machine name instead of a translation key.
import { useMemo, useState, type ReactElement } from 'react';
import { Button, Table, Tag, Tooltip, Typography } from 'antd';
import {
  TABLE_SORTER_TOOLTIP,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty } from '../../../shared/ui/quiet-states.js';
import type { ColumnType, TablePaginationConfig } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { formatDateTime } from '../lib/format.js';
import {
  ADMIN_PERIOD_PRESETS,
  makeAdminPeriodState,
  type AdminPeriodState,
} from '../lib/period.js';
import { useAdminAuditLog } from '../hooks/useAdminAudit.js';
import type { AdminAuditLogEntry } from '../api/admin.client.js';

const { Text } = Typography;

type ActionFilter = 'all' | string;

/** Actions written by P0/P1/P2 services; each has a translation key. */
const KNOWN_AUDIT_ACTIONS: readonly string[] = [
  'user.verify',
  'user.block',
  'user.unblock',
  'user.send_reset',
  'user.platform_role',
  'workspace.block',
  'workspace.unblock',
  'mail.retry',
  'plan_config.create',
  'plan_config.update',
  'plan_config.deactivate',
];

/** i18n key for an action: `user.block` → `crm.admin.audit.action.user_block`. */
function auditActionLabel(action: string, t: (key: string) => string): string {
  return KNOWN_AUDIT_ACTIONS.includes(action)
    ? t(`crm.admin.audit.action.${action.replace('.', '_')}`)
    : action;
}

const DEFAULT_PAGE_SIZE = 20;

export function AdminAuditPage(): ReactElement {
  const { t } = useTranslation();
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [period, setPeriod] = useState<AdminPeriodState>(() => makeAdminPeriodState('all'));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const auditQuery = useAdminAuditLog({
    page,
    pageSize,
    ...(actionFilter !== 'all' ? { action: actionFilter } : {}),
    ...(period.from !== undefined ? { from: period.from } : {}),
  });

  const total = auditQuery.data?.total ?? 0;
  const rows = auditQuery.data?.entries ?? [];

  const columns = useMemo<Array<ColumnType<AdminAuditLogEntry>>>(
    () => [
      {
        title: t('crm.admin.audit.col_time'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (value: string) => (
          <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
            {formatDateTime(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.audit.col_actor'),
        key: 'actor',
        width: 220,
        render: (_: unknown, row: AdminAuditLogEntry) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text className="font-mono" style={{ fontSize: 12 }} ellipsis>
              {row.actor.email}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
              {row.actor.name}
            </Text>
          </div>
        ),
      },
      {
        title: t('crm.admin.audit.col_action'),
        dataIndex: 'action',
        key: 'action',
        width: 190,
        render: (value: string) => (
          <Tooltip title={value}>
            <Tag className="font-mono" style={{ marginInlineEnd: 0, fontSize: 11 }}>
              {auditActionLabel(value, t)}
            </Tag>
          </Tooltip>
        ),
      },
      {
        title: t('crm.admin.audit.col_target'),
        key: 'target',
        width: 220,
        render: (_: unknown, row: AdminAuditLogEntry) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text style={{ fontSize: 12 }}>{row.targetType}</Text>
            {row.targetId !== null ? (
              <Text className="font-mono" type="secondary" style={{ fontSize: 11 }} ellipsis>
                {row.targetId}
              </Text>
            ) : null}
          </div>
        ),
      },
      {
        title: t('crm.admin.audit.col_metadata'),
        key: 'metadata',
        render: (_: unknown, row: AdminAuditLogEntry) => {
          if (row.metadata === null || row.metadata === undefined) {
            return (
              <Text disabled style={{ fontSize: 12 }}>
                —
              </Text>
            );
          }
          const json = JSON.stringify(row.metadata, null, 2);
          return (
            <Tooltip
              title={<pre className="m-0 max-h-64 overflow-auto text-xs font-mono">{json}</pre>}
            >
              <Text className="font-mono" style={{ fontSize: 11 }}>
                {'{…}'}
              </Text>
            </Tooltip>
          );
        },
      },
    ],
    [t],
  );

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: [20, 30, 50].map(String),
      onChange: (nextPage, nextPageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
      },
    }),
    [page, pageSize, total],
  );

  if (auditQuery.isError) {
    return (
      <ErrorState
        title={t('crm.admin.audit.load_error')}
        error={auditQuery.error}
        onRetry={() => void auditQuery.refetch()}
      />
    );
  }

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={t('crm.admin.audit.title')}
        subtitle={t('crm.admin.audit.subtitle', {
          visible: rows.length,
          total,
        })}
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void auditQuery.refetch()}
            loading={auditQuery.isFetching}
            aria-label={t('crm.admin.audit.reload')}
            style={LIST_CREATE_BUTTON_STYLE}
          >
            {t('crm.admin.audit.reload')}
          </Button>
        }
      />

      <div className="flex flex-col gap-3">
        <FilterBar>
          <FilterSelect
            aria-label={t('crm.admin.audit.action_filter_label')}
            value={actionFilter}
            onChange={(value) => {
              setActionFilter(value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: t('crm.admin.audit.filter_all') },
              ...KNOWN_AUDIT_ACTIONS.map((action) => ({
                value: action,
                label: auditActionLabel(action, t),
              })),
            ]}
          />
          <FilterSelect
            aria-label={t('crm.admin.common.period_label')}
            value={period.preset}
            onChange={(value) => {
              setPeriod(makeAdminPeriodState(value as AdminPeriodState['preset']));
              setPage(1);
            }}
            options={ADMIN_PERIOD_PRESETS.map((preset) => ({
              value: preset,
              label: t(`crm.admin.common.period_${preset}`),
            }))}
          />
        </FilterBar>

        <div style={LIST_SURFACE_STYLE}>
          <Table<AdminAuditLogEntry>
            showSorterTooltip={TABLE_SORTER_TOOLTIP}
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={auditQuery.isLoading}
            pagination={pagination}
            locale={{
              emptyText: (
                <QuietEmpty
                  text={
                    actionFilter !== 'all' || period.from !== undefined
                      ? t('crm.admin.audit.empty_search')
                      : t('crm.admin.audit.empty')
                  }
                />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
